import { FC, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Loader, Typography } from '@altermaind/web/ui';
import { AlertBadge } from '@/_shared/components/AlertBadge';
import { Charts } from '@/_shared/components/Charts';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { getEmployeesMeCall } from '@/profile/state/api';
import {
  getSurveyCampaignByIdResponsesResultsStatsCall,
  postEmployerByIdSurveyResultCall,
} from '@/surveys/state';

type Props = {
  campaignId?: string;
};

export const SurveyEmployeeResultsVsTeamVsDepartment: FC<Props> = ({ campaignId }) => {
  const { hasManagerLeader, hasTeamLeader } = getEmployeesMeCall.hook();
  const onlyTeamCompare = hasTeamLeader && !hasManagerLeader;
  const t = useTranslations(
    `department.details.assessment.${onlyTeamCompare ? 'resultsEmployeeVsTeam' : 'resultsEmployeeVsTeamVsDepartment'}`,
  );
  const tShared = useTranslations();
  const { isDesktop } = useBreakingPoints();

  const {
    teamStats,
    departmentsStats,
    organizationStats,
    execute: executeGetSurveyCampaignByIdResponsesResultsStats,
    loading: loadingGetSurveyCampaignByIdResponsesResultsStats,
    error: errorGetSurveyCampaignByIdResponsesResultsStats,
  } = getSurveyCampaignByIdResponsesResultsStatsCall.hook();

  const {
    response: responsePostEmployerByIdSurveyResult,
    success: successPostEmployerByIdSurveyResult,
  } = postEmployerByIdSurveyResultCall.hook();

  const { response: responseGetEmployeesMe } = getEmployeesMeCall.hook();

  const data = useMemo(() => {
    if (successPostEmployerByIdSurveyResult) {
      if (
        !departmentsStats?.[0].metricsScore ||
        !teamStats?.metricsScore ||
        !responsePostEmployerByIdSurveyResult
      ) {
        return [];
      }

      const employeeMetricsMap = new Map<string, number>();
      responsePostEmployerByIdSurveyResult.forEach(metric => {
        if (Array.isArray(metric?.results?.results)) {
          metric.results.results.forEach(result => {
            if (result.metric?.id) {
              employeeMetricsMap.set(result.metric.id, result.score || 0);
            }
          });
        }
      });

      const teamMetricsMap = new Map<string, number>();
      teamStats.metricsScore.forEach(orgMetric => {
        if (orgMetric.metric?.id) {
          teamMetricsMap.set(orgMetric.metric.id, orgMetric.score || 0);
        }
      });

      return departmentsStats?.[0].metricsScore.map(depMetric => {
        const valueEmployee = employeeMetricsMap.get(depMetric.metric?.id || '') || 0;
        const valueTeam = teamMetricsMap.get(depMetric.metric?.id || '') || 0;
        const valueDepartment = Number(depMetric?.score ?? 0);
        const diff = valueEmployee - Number(valueTeam);
        const diffDep = valueEmployee - valueDepartment;
        // eslint-disable-next-line no-nested-ternary
        const sign = diff > 0 ? '+' : diff < 0 ? '-' : '';
        // eslint-disable-next-line no-nested-ternary
        const signOrg = diffDep > 0 ? '+' : diffDep < 0 ? '-' : '';

        return {
          name: depMetric.metric?.name || '',
          value: valueEmployee,
          label:
            t('labelTooltipChart', {
              current: valueEmployee,
              teamValue: `${signOrg}${Math.abs(diff) === 0 ? 0 : Math.abs(diff).toFixed(1)} pt`,
              depValue: `${sign}${Math.abs(diff) === 0 ? 0 : Math.abs(diff).toFixed(1)} pt`,
            }) || '',
          value2: valueTeam || 0,
          value3: onlyTeamCompare ? undefined : valueDepartment,
        };
      });
    }
    return [];
  }, [
    successPostEmployerByIdSurveyResult,
    departmentsStats,
    teamStats,
    responsePostEmployerByIdSurveyResult,
    t,
    onlyTeamCompare,
  ]);

  useEffect(() => {
    if (campaignId) {
      executeGetSurveyCampaignByIdResponsesResultsStats({
        campaignId,
        departmentId: responseGetEmployeesMe?.department?.id,
        teamId: responseGetEmployeesMe?.area?.id || undefined,
      });
    }
  }, [campaignId, executeGetSurveyCampaignByIdResponsesResultsStats, responseGetEmployeesMe]);

  if (loadingGetSurveyCampaignByIdResponsesResultsStats) {
    return (
      <Box mt='s' textAlign='center'>
        <Loader size='s' />
      </Box>
    );
  }

  if (errorGetSurveyCampaignByIdResponsesResultsStats) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  if (
    Array.isArray(departmentsStats?.[0]?.metricsScore) &&
    departmentsStats?.[0].metricsScore.length > 0
  ) {
    return (
      <Box>
        <Typography variant='h3'>{t('title')}</Typography>
        <Typography variant='p3/light'>{t('description')}</Typography>
        <Box mt='xxl' display='flex' flexDirection='row' alignItems='stretch'>
          <Box flex='1' mr='s' display='flex' alignItems='center'>
            <Charts.BarChartComponent
              domain={[0, 7]}
              hasMultipleValues
              domainHasInPercentage={false}
              data={data}
              defaultItemsSize={isDesktop ? 6 : 2}
            />
          </Box>
        </Box>
        <Box mt='s' display='flex' justifyContent='center'>
          <Box justifyContent='start' width='100%' display='flex' flexDirection='row'>
            <Box alignContent='center' display='flex' alignItems='center' mr='m'>
              <Box
                width='xxs'
                height='xxs'
                backgroundColor='primary'
                borderRadius='50%'
                mr='xxxs'
              />
              <Typography variant='p2/light'>{t('legend.employee')}</Typography>
            </Box>
            <Box alignContent='center' display='flex' alignItems='center' mr='m'>
              <Box
                width='xxs'
                height='xxs'
                backgroundColor={'rgba(176, 128, 255, 1)' as never}
                borderRadius='50%'
                mr='xxxs'
              />
              <Typography variant='p2/light'>
                {t('legend.teamAverage', {
                  total: departmentsStats?.[0]?.totalCompletedSurveys?.toString() || '',
                })}
              </Typography>
            </Box>
            {!onlyTeamCompare ? (
              <Box alignContent='center' display='flex' alignItems='center' mr='m'>
                <Box
                  width='xxs'
                  height='xxs'
                  backgroundColor='white'
                  borderRadius='50%'
                  mr='xxxs'
                />
                <Typography variant='p2/light'>
                  {t('legend.depAverage', {
                    total: organizationStats?.totalCompletedSurveys?.toString() || '',
                  })}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  }

  return null;
};
